/* eslint-disable sonarjs/no-duplicate-string */
import { Widget, WidgetType } from '@/api/rest/resources/types/widget';

import { FACT_AGGREATION_METHOD, IndicatorFactType } from './fact';

export enum IndicatorTiersEnum {
  free = 'free',
  add_on = 'add_on',
  lite = 'lite',
  pro = 'pro',
}

export type IndicatorTiersType = keyof typeof IndicatorTiersEnum;

export enum VerifiedStatusEnum {
  verified = 'verified',
  not_verified = 'not_verified',
  pending = 'pending',
}

export type VerifiedStatusType = keyof typeof VerifiedStatusEnum;

export enum VerifiedByEnum {
  third_party_verified = 'third_party_verified',
  self_verified = 'self_verified',
}

export type VerifiedByType = keyof typeof VerifiedByEnum;

export type IndicatorProduct = {
  id: string;
  purchased_at: Date | null;
  tier?: string;
  name: string;
  widget: {
    type: WidgetType;
    widget_format: {
      overview?: {
        title?: string;
        pill?: string;
      };
      data_sources?: string[];
      details?: {
        verification?: string;
        financial_modelling?: string;
        measurement_protocol?: string;
        definition_and_scientific_basis?: string;
      };
    };
  };
  verification_information?: {
    status?: string;
    verified_by?: string;
  };
  count_of_applied_assets?: number;
  show_calculation_progress?: boolean;
  is_recommended?: boolean;
  indicator_names: IndicatorFactType[];
};

export type IndicatorProductCalculationProgres = {
  indicator_product_id: string;
  expected_fact_count: number;
  completed_fact_count: number;
  remaining_fact_count: number;
  failed_fact_count: number;
};

export const INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  WAITING: 'waiting',
  ERROR: 'error',
  VALIDATED: 'validated',
} as const;

export const INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE = {
  DEFAULT: 'default',
  MEDIA_MAP: 'media_map',
};

export type InsituDataFile = {
  id: string;
  visible_filename: string;
  content_type: string;
  url: string;
  modified_at: Date;
};

export type IndicatorProductSubscriptionConfiguration = {
  id: string;
  file_info: InsituDataFile[] | null;
  aggregation_method: (typeof FACT_AGGREATION_METHOD)[keyof typeof FACT_AGGREATION_METHOD];
  status: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS];
  indicator_name: string;
  widget: Widget;
  organization: string;
  indicator_product_subscription: string;
  created_at: Date;
  updated_at: Date;
  validation_errors: string[];
  description: string;
  template_url?: string;
  template?: string;
  modified_at: string;
  type: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE];
};

export enum INDICATOR_PRODUCT_NAME {
  LANDSCAPE_PROTECTED_AREAS_IBAT = 'landscape_protected_areas_wdpa',
  LANDSCAPE_PROTECTED_AREAS_OSM = 'landscape_protected_areas_osm',
  LANDSCAPE_THREATENED_SPECIES = 'landscape_species_presence',
}

export const INDICATOR = {
  // BE defined Indicator Products
  CARBON: 'carbon', // below ground carbon
  ABOVE_GROUND_CARBON: 'above_ground_carbon',
  ESA_ABOVE_GROUND_CARBON: 'esa_above_ground_carbon',
  WATER_HIGH_RESOLUTION: 'water_high_resolution',
  WATER_LOW_RESOLUTION: 'water_low_resolution',
  PROTECTED_ON_FARM_HABITAT: 'protected_on_farm_habitat',
  LANDSCAPE_PROTECTED_AREAS_OSM: 'landscape_protected_areas_osm',
  LANDSCAPE_PROTECTED_AREAS_WDPA: 'landscape_protected_areas_wdpa',
  LANDSCAPE_SPECIES_PRESENCE: 'landscape_species_presence',
  FREE_LANDSCAPE_SPECIES: 'free_landscape_species',
  SOCIAL_LIFE_EXPECTANCY: 'social_life_expectancy',
  EO_VEGETATION_CONDITION: 'eo_vegetation_condition',
  FREE_CLIMATE_RISK: 'free_climate_risk',
  LANDSCAPE_LANDCOVER_LANDUSE: 'landscape_landcover_landuse',
  MONETARY_RISK: 'monetary_risk',
  DEFORESTATION: 'deforestation',
  FOREST_INTELLIGENCE: 'forest_intelligence',
  COOLFARMTOOL_CO2_SPATIALISE: 'coolfarmtool_co2_spatialise',
  CLIMATE_RISK: 'climate_risk',
  HABITAT_INTACTNESS: 'habitat_intactness',
  ECOSYSTEM_INTEGRITY: 'ecosystem_integrity',
  STRUCTURAL_INTEGRITY: 'structural_integrity',
  CONSERVATION: 'conservation',
  IN_SITU_PACK: 'in_situ_pack',
  COMBINED_FACTS: 'combined_facts', // below and above ground carbon
  SM_TREND: 'sm_trend', // precipitation + soil moisture
  LST_ANALYZE: 'lst_analyze',

  // FE defined Indicator Products (some are part of other Indicator Products but the FE needs to handle them separately)
  CANOPY_COVER: 'canopy_cover',
  CANOPY_HEIGHT: 'canopy_height',
  PRECIPITATION: 'precipitation',
  SOIL_MOISTURE: 'soil_moisture',
  HABITAT_INTACTNESS_THREATS_BREAKDOWN: 'habitat_intactness_threats_breakdown',
  POPULATION_DENSITY: 'population_density',
  CLEANWAVE: 'cleanwave',
} as const;

export const INDICATOR_DOMAINS = {
  BIODIVERSITY: 'biodiversity',
  CARBON: 'carbon',
  WATER: 'water',
  SOCIAL: 'social',
  SOIL: 'soil',
} as const;

export const INDICATOR_PRODUCTS_BY_DOMAIN = {
  [INDICATOR_DOMAINS.CARBON]: [
    INDICATOR.ABOVE_GROUND_CARBON,
    INDICATOR.ESA_ABOVE_GROUND_CARBON,
    INDICATOR.CARBON,
    INDICATOR.COOLFARMTOOL_CO2_SPATIALISE,
    INDICATOR.COMBINED_FACTS,
  ],
  [INDICATOR_DOMAINS.WATER]: [
    INDICATOR.SOIL_MOISTURE,
    INDICATOR.PRECIPITATION,
    INDICATOR.SM_TREND,
    INDICATOR.WATER_HIGH_RESOLUTION,
    INDICATOR.WATER_LOW_RESOLUTION,
  ],
  [INDICATOR_DOMAINS.BIODIVERSITY]: [
    INDICATOR.PROTECTED_ON_FARM_HABITAT,
    INDICATOR.HABITAT_INTACTNESS,
    INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN,
    INDICATOR.STRUCTURAL_INTEGRITY,
    INDICATOR.EO_VEGETATION_CONDITION,
    INDICATOR.DEFORESTATION,
    INDICATOR.FOREST_INTELLIGENCE,
  ],
  [INDICATOR_DOMAINS.SOIL]: [INDICATOR.CARBON, INDICATOR.COMBINED_FACTS],
  [INDICATOR_DOMAINS.SOCIAL]: [INDICATOR.SOCIAL_LIFE_EXPECTANCY],
};

export const INDICATOR_PRODUCTS_BY_DOMAIN_PLOT_LEVEL = {
  [INDICATOR_DOMAINS.CARBON]: [
    INDICATOR.ABOVE_GROUND_CARBON,
    INDICATOR.ESA_ABOVE_GROUND_CARBON,
    INDICATOR.CARBON,
    INDICATOR.CANOPY_COVER,
    INDICATOR.CANOPY_HEIGHT,
    INDICATOR.COOLFARMTOOL_CO2_SPATIALISE,
    INDICATOR.COMBINED_FACTS,
  ],
  [INDICATOR_DOMAINS.WATER]: [
    INDICATOR.SOIL_MOISTURE,
    INDICATOR.PRECIPITATION,
    INDICATOR.SM_TREND,
    INDICATOR.WATER_HIGH_RESOLUTION,
    INDICATOR.WATER_LOW_RESOLUTION,
  ],
  [INDICATOR_DOMAINS.BIODIVERSITY]: [
    INDICATOR.PROTECTED_ON_FARM_HABITAT,
    INDICATOR.HABITAT_INTACTNESS,
    INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN,
    INDICATOR.STRUCTURAL_INTEGRITY,
    INDICATOR.EO_VEGETATION_CONDITION,
    INDICATOR.DEFORESTATION,
    INDICATOR.FOREST_INTELLIGENCE,
    INDICATOR.ECOSYSTEM_INTEGRITY,
    INDICATOR.LANDSCAPE_PROTECTED_AREAS_OSM,
    INDICATOR.LANDSCAPE_PROTECTED_AREAS_WDPA,
    INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN,
    INDICATOR.LANDSCAPE_SPECIES_PRESENCE,
    INDICATOR.FREE_LANDSCAPE_SPECIES,
    INDICATOR.LANDSCAPE_LANDCOVER_LANDUSE,
  ],
  [INDICATOR_DOMAINS.SOIL]: [INDICATOR.CARBON, INDICATOR.COMBINED_FACTS, INDICATOR.COOLFARMTOOL_CO2_SPATIALISE],
  [INDICATOR_DOMAINS.SOCIAL]: [INDICATOR.SOCIAL_LIFE_EXPECTANCY],
};

/**
 * GET /api/v1/dashboard/indicator-product
 */
export const GET_INDICATOR_PRODUCTS_PATH = 'dashboard/indicator-product';
export type GetIndicatorProductsRequestPathVariables = undefined;
export type GetIndicatorProductsRequestQueryParams = {
  organization_id: string;
  purchased?: boolean;
  visible_for_customer?: boolean;
};
export type GetIndicatorProductsSuccessDto = IndicatorProduct[];
export type GetIndicatorProductsErrorDto = Error;

/**
 * POST /api/v1/dashboard/indicator-product/subscribe
 */
export const POST_INDICATOR_PRODUCT_SUBSCRIBE_PATH = 'dashboard/indicator-product/subscribe';
export type PostIndicatorProductSubscribeRequestPathVariables = undefined;
export type PostIndicatorProductSubscribeRequestQueryParams = undefined;
export type PostIndicatorProductSubscribeBodyData = { organization_id: string; indicator_product_ids: string[] };
export type PostIndicatorProductSubscribeSuccessDto = {
  subscriptions: {
    indicator_product_id: string;
    subscription_id: string;
    success: boolean;
    error: string;
  }[];
  count_of_applied_assets: number;
  total_subscriptions_created: number;
  total_subscriptions_failed: number;
};
export type PostIndicatorProductSubscribeErrorDto = Error;

/**
 * GET /api/v1/dashboard/indicator-product/calculation-progress
 */
export const GET_INDICATOR_PRODUCTS_CALCULATION_PROGRESS_PATH = 'dashboard/indicator-product/calculation-progress';
export type GetIndicatorProductsCalculationProgressRequestPathVariables = undefined;
export type GetIndicatorProductsCalculationProgressRequestQueryParams = {
  indicator_product_id: string;
  organization_id: string;
};
export type GetIndicatorProductsCalculationProgressSuccessDto = IndicatorProductCalculationProgres;
export type GetIndicatorProductsCalculationProgressErrorDto = Error;

/**
 * GET /api/v1/dashboard/co-pilot-config/{organization_id}/recommended-products/
 */
export const GET_ORGANIZATION_RECOMMENDED_INDICATOR_PRODUCTS_PATH =
  'dashboard/co-pilot-config/{organization_id}/recommended-products';
export type GetOrganizationRecommendedIndicatorProductsRequestPathVariables = { organization_id: string };
export type GetOrganizationRecommendedIndicatorProductsRequestQueryParams = undefined;
export type GetOrganizationRecommendedIndicatorProductsSuccessDto = IndicatorProduct[];
export type GetOrganizationRecommendedIndicatorProductsErrorDto = Error;

/**
 * GET /api/v1/dashboard/indicator-product-subscription/
 */
export const GET_INDICATOR_PRODUCT_SUBSCRIPTION_PATH = 'dashboard/indicator-product-subscription';
export type GetIndicatorProductSubscriptionRequestPathVariables = undefined;
export type GetIndicatorProductSubscriptionRequestQueryParams = {
  organization_id: string;
};
export type GetIndicatorProductSubscriptionSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: string;
    organization: string;
    indicator_product: IndicatorProduct;
    created_at: Date;
    updated_at: Date;
  }[];
};

export type GetIndicatorProductSubscriptionErrorDto = Error;

/**
 * GET /api/v1/dashboard/indicator-product-subscription-configuration/
 */
export const GET_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATIONS_PATH =
  'dashboard/indicator-product-subscription-configuration';
export type GetIndicatorProductSubscriptionConfigurationsRequestPathVariables = undefined;
export type GetIndicatorProductSubscriptionConfigurationsRequestQueryParams = {
  organization_id: string;
  file_info_latest: boolean;
  status?: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS];
  type?: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE];
};
export type GetIndicatorProductSubscriptionConfigurationsSuccessDto = {
  count: number;
  next: string | null;
  previous: string | null;
  results: IndicatorProductSubscriptionConfiguration[];
};
export type GetIndicatorProductSubscriptionConfigurationsErrorDto = Error;

/**
 * GET /api/v1/dashboard/indicator-product-subscription-configuration/{id}/
 */
export const GET_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_BY_ID_PATH =
  'dashboard/indicator-product-subscription-configuration/{id}';
export type GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestPathVariables = { id: string };
export type GetDashboardIndicatorProductSubscriptionConfigurationByIdRequestQueryParams = {
  organization_id: string;
};
export type GetDashboardIndicatorProductSubscriptionConfigurationByIdSuccessDto =
  IndicatorProductSubscriptionConfiguration;
export type GetDashboardIndicatorProductSubscriptionConfigurationByIdErrorDto = Error;

/**
 * POST /api/v1/dashboard/indicator-product-subscription-configuration/
 */
export const POST_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_PATH =
  'dashboard/indicator-product-subscription-configuration';
export type PostDashboardIndicatorProductSubscriptionConfigurationRequestPathVariables = undefined;
export type PostDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams = undefined;
export type PostDashboardIndicatorProductSubscriptionConfigurationBodyData = Pick<
  IndicatorProductSubscriptionConfiguration,
  | 'aggregation_method'
  | 'status'
  | 'indicator_name'
  | 'organization'
  | 'indicator_product_subscription'
  | 'template'
  | 'type'
>;
export type PostDashboardIndicatorProductSubscriptionConfigurationSuccessDto =
  IndicatorProductSubscriptionConfiguration;
export type PostDashboardIndicatorProductSubscriptionConfigurationErrorDto = Error;

/**
 * PATCH /api/v1/dashboard/indicator-product-subscription-configuration/{id}/
 */
export const PATCH_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_PATH =
  'dashboard/indicator-product-subscription-configuration/{id}';
export type PatchDashboardIndicatorProductSubscriptionConfigurationRequestPathVariables = { id: string };
export type PatchDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams = {
  organization_id: string;
};
export type PatchDashboardIndicatorProductSubscriptionConfigurationBodyData =
  Partial<IndicatorProductSubscriptionConfiguration>;
export type PatchDashboardIndicatorProductSubscriptionConfigurationSuccessDto =
  IndicatorProductSubscriptionConfiguration;
export type PatchDashboardIndicatorProductSubscriptionConfigurationErrorDto = Error;

/**
 * DELETE /api/v1/dashboard/indicator-product-subscription-configuration/{id}/
 */
export const DELETE_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_PATH =
  'dashboard/indicator-product-subscription-configuration/{id}';
export type DeleteDashboardIndicatorProductSubscriptionConfigurationRequestPathVariables = { id: string };
export type DeleteDashboardIndicatorProductSubscriptionConfigurationRequestQueryParams = {
  organization_id: string;
};
export type DeleteDashboardIndicatorProductSubscriptionConfigurationSuccessDto = undefined;
export type DeleteDashboardIndicatorProductSubscriptionConfigurationErrorDto = Error;

/**
 * POST /api/v1/dashboard/indicator-product-subscription-configuration/file-upload/
 */
export const POST_INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_FILE_UPLOAD_PATH =
  'dashboard/indicator-product-subscription-configuration/file-upload';
export type PostDashboardIndicatorProductSubscriptionConfigurationFileUploadRequestPathVariables = undefined;
export type PostDashboardIndicatorProductSubscriptionConfigurationFileUploadRequestQueryParams = {
  organization_id: string;
};
export type PostDashboardIndicatorProductSubscriptionConfigurationFileUploadBodyData = FormData;
export type PostDashboardIndicatorProductSubscriptionConfigurationFileUploadSuccessDto = {
  id: string;
  indicator_product_subscription_configuration_id: string;
  file_info: File;
  description?: string;
};
export type PostDashboardIndicatorProductSubscriptionConfigurationFileUploadErrorDto = {
  errors?: string[];
} & Error;
